import { Converter } from "showdown"

import { MARKDOWN_IMAGE, unwrapDestination } from "./markdown-image"

// Extended_Pictographic covers the emoji block itself; the rest are the
// modifiers that ride along with one: skin tones, ZWJ sequences, the
// emoji-style variation selector, flags, and keycap digits like 1⃣.
const EMOJI_PATTERN =
	// eslint-disable-next-line no-misleading-character-class -- intentionally matching invisible/combining characters
	/[\p{Extended_Pictographic}\p{Emoji_Modifier}\p{Regional_Indicator}\u200d\ufe0f\u20e3]/gu

/** Drops every emoji from a string, then tidies the whitespace it leaves behind. */
export function stripEmojis(text: string): string {
	return (
		text
			.replace(EMOJI_PATTERN, "")
			.replace(/[ \t]{2,}/g, " ")
			// An emoji right before punctuation leaves a dangling space: "you 🫣," -> "you ,".
			.replace(/[ \t]+([,.;:!?])/g, "$1")
			.replace(/^[ \t]+|[ \t]+$/gm, "")
			.trim()
	)
}

// Showdown swaps html spans for `¨C11C` style hashes while it converts and puts
// them back at the end, but it gives up past ten nested spans and leaves the
// hash standing. Nothing a reader wrote looks like one.
const SHOWDOWN_HASH = /¨[A-Z]\d+[A-Z]/g

/** Drops the hash placeholders showdown failed to put back. */
export function stripShowdownHashes(html: string): string {
	return html.replace(SHOWDOWN_HASH, "")
}

const converter = new Converter()

// A plain-numbered turndown list: "1.  text" with wrapped continuation lines
// indented under the marker. Anything looser (nested lists, code fences,
// blank lines inside an item) is left alone rather than risk mangling it.
const LIST_BLOCK = /(?:^\d+\.[ \t]+.*(?:\n(?:[ \t]{2,}\S.*|[ \t]*))*)+/gm
const ITEM_START = /^(\d+)\.[ \t]+(.*)$/

interface ParsedItem {
	number: number
	text: string
}

function parseListBlock(block: string): ParsedItem[] | null {
	const lines = block.split("\n")
	const items: ParsedItem[] = []

	for (const line of lines) {
		const start = ITEM_START.exec(line)
		if (start) {
			items.push({ number: Number(start[1]), text: start[2] ?? "" })
			continue
		}
		const trimmed = line.trim()
		if (trimmed === "") continue
		const current = items[items.length - 1]
		// A continuation line with no open item, or a nested/deeper structure
		// (its own list marker, a fence), isn't the simple shape this handles.
		if (!current || /^(?:[-*+]|\d+\.|```)/.test(trimmed)) return null
		current.text += ` ${trimmed}`
	}

	return items.length > 0 ? items : null
}

function itemToHtml(text: string): string {
	// makeHtml wraps a single paragraph in <p>...</p>, which is exactly the
	// shape Tiptap expects inside a step <li>.
	return converter.makeHtml(text.trim()).trim()
}

/**
 * Rewrites every plain numbered list in the markdown into the raw
 * `<ol class="steps-list">` HTML the step-badge treatment keys off. Markdown
 * has no syntax for that class, so a plain `1.` list would otherwise render
 * as an ordinary numbered list once it reaches the editor.
 */
export function applyStepsListStyle(markdown: string): string {
	return markdown.replace(LIST_BLOCK, (block) => {
		const items = parseListBlock(block)
		if (!items) return block

		const first = items[0]?.number ?? 1
		const startAttr = first !== 1 ? ` start="${first}"` : ""
		const style = first > 1 ? ` style="counter-reset: step ${first - 1}"` : ""

		const li = items
			.map((item) => `<li>${itemToHtml(item.text)}</li>`)
			.join("\n")

		// A blank line has to follow, or marked treats whatever comes next as
		// still inside this raw HTML block (per CommonMark's HTML-block rule)
		// and never runs it through markdown at all: **bold** and [links](x)
		// stay literal text, then get escaped defensively on the next read.
		return `<ol class="steps-list"${startAttr}${style}>\n${li}\n</ol>\n\n`
	})
}

/**
 * Swaps a scraped screenshot for its backgrounded, re-hosted version. Markdown
 * has no room for the original-vs-processed pair the editor toggle needs, so
 * the result is raw `<img>` HTML carrying both: `src` shows the processed
 * image by default, and `data-original-src`/`data-processed-src` are the
 * same pair the in-app "Add background" button writes, so the article still
 * opens with the "Remove background" toggle available in the dashboard.
 */
export function applyImageBackgrounds(
	markdown: string,
	processedByOriginal: ReadonlyMap<string, string>,
): string {
	return markdown.replace(
		MARKDOWN_IMAGE,
		(whole, alt: string, destination: string) => {
			const url = unwrapDestination(destination)
			const processed = processedByOriginal.get(url)
			if (!processed) return whole

			const altAttr = alt ? ` alt="${alt.replace(/"/g, "&quot;")}"` : ""
			return `<img src="${processed}" data-original-src="${url}" data-processed-src="${processed}"${altAttr}>`
		},
	)
}
